import { FunctionComponent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLazyQuery, useQuery } from '@apollo/client'
import { GET_PARENT } from '@/modules/API/parent/parentHandler'
import styles from './parentProfile.module.scss'
import Loading from '@/components/Loading/Loading'
import Title from '@/components/_custom/Title/title'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { Role } from '@/types/Role'
import UserInput from '@/components/_custom/UserInput/userInput'
import { KeyboardArrowDown, KeyboardArrowUp, LinkOff, LinkSharp } from '@mui/icons-material'
import DetachFamilyDialog from './Dialog/detachFamilyDialog'
import moment from 'moment-timezone'
import { Child } from '@/types/Child'
import { GET_SECTIONS } from '@/modules/API/daycare/sectionHandler'
import { GET_GROUPS } from '@/modules/API/daycare/groupHandler'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { useAuth } from '@/modules/context/AuthContext'
import ConfirmAssociateParentDialog from '../../suggest/suggestTable/suggestParentTable/Dialog/confirmAssociateParentDialog'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import RpeComplementaryInformations from './rpeComplementary/rpeComplementaryInformations'
import { ParentDaycare } from '@/types/Parent'
import ChildTable from './childTable/childTable'
import { SelectOption, SelectOptions } from '@/types/Select'
import { Section } from '@/types/Section'
import { Group } from '@/types/Group'
import TaskAndNotesParent from './taskAndNotes/taskAndNotesParent.tsx'
import { Daycare } from '@/types/Daycare.ts'

interface ParentProfileProps {
    parentId: number
}

const ParentProfile: FunctionComponent<ParentProfileProps> = ({ parentId }: ParentProfileProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const navigate: NavigateFunction = useNavigate()

    const [showPersonalInformation, setShowPersonalInformation] = useState<boolean>(true)

    const [userId, setUserId] = useState<number>(0)
    const [created, setCreated] = useState<string>('')

    const [email, setEmail] = useState<string>('')
    const [firstname, setFirstname] = useState<string>('')
    const [lastname, setLastname] = useState<string>('')
    const [phone, setPhone] = useState<string>('')
    const [phoneSecond, setPhoneSecond] = useState<string>('')

    const [showAddress, setShowAddress] = useState<boolean>(false)

    const [address, setAddress] = useState<string>('')
    const [zip, setZip] = useState<string>('')
    const [city, setCity] = useState<string>('')
    const [country, setCountry] = useState<string>('')

    const [showAddressSecond, setShowAddressSecond] = useState<boolean>(false)

    const [addressSecond, setAddressSecond] = useState<string>('')
    const [zipSecond, setZipSecond] = useState<string>('')
    const [citySecond, setCitySecond] = useState<string>('')
    const [countrySecond, setCountrySecond] = useState<string>('')

    const [isNanny, setIsNanny] = useState<boolean>(false)

    const [sections, setSections] = useState<SelectOptions<number>>([])
    const [groups, setGroups] = useState<SelectOptions<number>>([])

    const [childs, setChilds] = useState<Array<Child>>([])
    const [parentDaycaresList, setParentDaycaresList] = useState<ParentDaycare[]>([])
    const [parentDaycare, setParentDaycare] = useState<ParentDaycare | undefined>()

    const [selectOptionsDaycare, setSelectOptionsDaycare] = useState<SelectOptions<number>>([])

    const [isDetachFamilyDialog, setDetachFamilyDialog] = useState<boolean>(false)

    const [isConfirmAssociateDialogOpen, setConfirmAssociateDialogOpen] = useState<boolean>(false)

    function handleGoBack(): void {
        navigate(-1)
    }

    const {
        data: dataParent,
        loading: loadingParent,
        error: errorParent,
        refetch: refetchParent
    } = useQuery(GET_PARENT, {
        variables: { parent_id: parentId }
    })

    useEffect(() => {
        if (dataParent) {
            setEmail(dataParent.getParent.email)
            setFirstname(dataParent.getParent.firstname)
            setLastname(dataParent.getParent.lastname)
            setPhone(dataParent.getParent.phone)
            setPhoneSecond(dataParent.getParent.phoneSecond)
            setChilds(dataParent.getParent.childs)
            setAddress(dataParent.getParent.address)
            setZip(dataParent.getParent.zip)
            setCity(dataParent.getParent.city)
            setCountry(dataParent.getParent.country)
            setAddressSecond(dataParent.getParent.addressSecond)
            setZipSecond(dataParent.getParent.zipSecond)
            setCitySecond(dataParent.getParent.citySecond)
            setCountrySecond(dataParent.getParent.countrySecond)
            setIsNanny(dataParent.getParent.isNanny)
            if (dataParent.getParent.daycares && dataParent.getParent.daycares.length > 0) {
                const allDaycares: ParentDaycare[] = dataParent.getParent.daycares

                setParentDaycaresList(allDaycares)
                setSelectOptionsDaycare([
                    {
                        options: allDaycares.map((daycare: ParentDaycare): SelectOption<number> => {
                            return {
                                name: daycare.daycareCity + ' - ' + daycare.daycareName,
                                value: daycare.daycareId
                            }
                        })
                    }
                ])
            }
            if (user?.role === Role.cald) {
                setUserId(dataParent.getParent.userId)
                setCreated(moment(dataParent.getParent.created, 'DD-MM-YYYY HH:mm:ss').format('DD-MM-YYYY'))
            }
        }
    }, [dataParent])

    useEffect(() => {
        if (errorParent) {
            handleGoBack()
        }
    }, [errorParent])

    async function handleRefetchParent(): Promise<void> {
        const { data } = await refetchParent()
        if (data) {
            setEmail(data.getParent.email)
            setFirstname(data.getParent.firstname)
            setLastname(data.getParent.lastname)
            setPhone(data.getParent.phone)
            setPhoneSecond(data.getParent.phoneSecond)
            setChilds(data.getParent.childs)
            setAddress(data.getParent.address)
            setZip(data.getParent.zip)
            setCity(data.getParent.city)
            setAddressSecond(data.getParent.addressSecond)
            setZipSecond(data.getParent.zipSecond)
            setCitySecond(data.getParent.citySecond)
            setIsNanny(data.getParent.isNanny)
            if (data.getParent.daycares && data.getParent.daycares.length > 0) {
                const allDaycares: ParentDaycare[] = data.getParent.daycares

                setParentDaycaresList(allDaycares)
                setSelectOptionsDaycare([
                    {
                        options: allDaycares.map((daycare: ParentDaycare): SelectOption<number> => {
                            return {
                                name: daycare.daycareCity + ' - ' + daycare.daycareName,
                                value: daycare.daycareId
                            }
                        })
                    }
                ])
            }
            if (user?.role === Role.cald) {
                setUserId(data.getParent.userId)
                setCreated(moment(data.getParent.created, 'DD-MM-YYYY HH:mm:ss').format('DD-MM-YYYY'))
            }
        }
    }

    function handleSetSelectedDaycare(id: number): void {
        const daycare: ParentDaycare | undefined = parentDaycaresList.find(
            (daycare: ParentDaycare): boolean => daycare.daycareId === id
        )
        if (daycare) {
            setParentDaycare(daycare)
        }
    }

    useEffect(() => {
        if (parentDaycaresList && user) {
            for (const tempDaycare of parentDaycaresList) {
                const daycareFound: Daycare | undefined = user.daycares.find(
                    (daycare: Daycare): boolean => daycare.id === tempDaycare.daycareId
                )
                if (daycareFound) {
                    if (user.currentDaycare?.id === daycareFound.id) {
                        setParentDaycare(tempDaycare)
                    } else if (parentDaycare === undefined) {
                        setParentDaycare(tempDaycare)
                    }
                }
            }
        }
    }, [parentDaycaresList])

    useEffect(() => {
        if (parentDaycare) {
            getSections({
                variables: {
                    daycare_id: parentDaycare.daycareId
                }
            })
            getGroups({
                variables: {
                    daycare_id: parentDaycare.daycareId
                }
            })
        }
    }, [parentDaycare])

    const [getSections, { loading: loadingSections, data: dataSections }] = useLazyQuery<{ getSections: Section[] }>(
        GET_SECTIONS,
        {
            variables: {
                daycare_id: parentDaycare?.daycareId
            }
        }
    )

    useEffect(() => {
        if (dataSections) {
            setSections([
                {
                    options: dataSections.getSections.map((section: Section) => ({
                        value: section.id,
                        name: section.name
                    }))
                }
            ])
        }
    }, [dataSections])

    const [getGroups, { loading: loadingGroups, data: dataGroups }] = useLazyQuery<{ getGroups: Group[] }>(GET_GROUPS, {
        variables: {
            daycare_id: parentDaycare?.daycareId
        }
    })

    useEffect(() => {
        if (dataGroups) {
            setGroups([
                {
                    options: dataGroups.getGroups.map((group: Group) => ({
                        value: group.id,
                        name: group.name
                    }))
                }
            ])
        }
    }, [dataGroups])

    return (
        <>
            {(loadingParent || loadingSections || loadingGroups) && <Loading overall />}
            <div className={styles.PagePosition}>
                {user?.role === Role.cald && parentDaycaresList.length <= 1 && (
                    <>
                        {parentDaycaresList.length === 0 ? (
                            <div className={styles.NoDaycareCALD}>
                                <ErrorMessage error={t('noDaycare')} className={styles.Error} />
                            </div>
                        ) : (
                            parentDaycare &&
                            user.currentDaycare &&
                            parentDaycare.daycareId !== user.currentDaycare.id && (
                                <div className={styles.NoDaycareCALD}>
                                    <ErrorMessage
                                        error={parentDaycare.daycareCity + ' - ' + parentDaycare.daycareName}
                                        className={styles.Info}
                                    />
                                </div>
                            )
                        )}
                    </>
                )}
                <div className={styles.ProfilPlacement}>
                    {dataParent && (
                        <div className={styles.Form}>
                            <div className={styles.TitleAndButton}>
                                <div className={styles.Title}>
                                    <Title
                                        text={`${t('personalInformation')}
										${user?.role === Role.cald || parentDaycare?.isRpeOrLaep ? '- ' + t(isNanny ? 'assMatShort' : 'parent') : ''}`}
                                    />
                                    {showPersonalInformation ? (
                                        <KeyboardArrowUp
                                            onClick={() => setShowPersonalInformation(false)}
                                            className={styles.Arrow}
                                        />
                                    ) : (
                                        <KeyboardArrowDown
                                            onClick={() => setShowPersonalInformation(true)}
                                            className={styles.Arrow}
                                        />
                                    )}
                                </div>
                            </div>
                            {showPersonalInformation && (
                                <>
                                    <div className={styles.InputContainer}>
                                        {user?.role === Role.cald && userId && (
                                            <div className={styles.Input}>
                                                <UserInput
                                                    label={t('userId')}
                                                    value={userId.toString()}
                                                    readOnly
                                                    className={styles.UserInput}
                                                />
                                                <UserInput
                                                    label={t('creationDate')}
                                                    value={created}
                                                    readOnly
                                                    className={styles.UserInput}
                                                />
                                            </div>
                                        )}
                                        <div className={styles.Input}>
                                            <UserInput
                                                label={t('firstname')}
                                                value={firstname}
                                                setValue={setFirstname}
                                                readOnly
                                                className={styles.UserInput}
                                            />
                                            <UserInput
                                                label={t('lastname')}
                                                value={lastname}
                                                setValue={setLastname}
                                                readOnly
                                                className={styles.UserInput}
                                            />
                                        </div>
                                        <div className={styles.Input}>
                                            <UserInput
                                                label={email && t('email')}
                                                value={email ? email : t('withoutAccount')}
                                                setValue={setEmail}
                                                readOnly
                                                type={email ? 'email' : 'text'}
                                                className={styles.UserInput}
                                            />
                                            <UserInput
                                                label={t('phone')}
                                                value={phone}
                                                setValue={setPhone}
                                                readOnly
                                                type={'phone'}
                                                className={styles.UserInput}
                                            />
                                        </div>
                                        {phoneSecond && (
                                            <div className={styles.Input}>
                                                <UserInput
                                                    label={t('phoneSecond')}
                                                    value={phoneSecond}
                                                    setValue={setPhoneSecond}
                                                    type={'phone'}
                                                    readOnly
                                                    className={styles.UserInputAlone}
                                                />
                                            </div>
                                        )}
                                    </div>
                                    <div className={styles.TitleAlone}>
                                        <Title text={t('address')} />
                                        {showAddress ? (
                                            <KeyboardArrowUp
                                                onClick={() => setShowAddress(false)}
                                                className={styles.Icon}
                                            />
                                        ) : (
                                            <KeyboardArrowDown
                                                onClick={() => setShowAddress(true)}
                                                className={styles.Icon}
                                            />
                                        )}
                                    </div>
                                    {showAddress && (
                                        <>
                                            <div className={styles.InputContainer}>
                                                <div className={styles.Input}>
                                                    <UserInput
                                                        label={t('address')}
                                                        value={address}
                                                        setValue={setAddress}
                                                        readOnly
                                                        className={styles.UserInput}
                                                    />
                                                    <UserInput
                                                        label={t('city')}
                                                        value={city}
                                                        setValue={setCity}
                                                        readOnly
                                                        className={styles.UserInput}
                                                    />
                                                </div>
                                                <div className={styles.Input}>
                                                    <UserInput
                                                        label={t('zipCode')}
                                                        value={zip}
                                                        setValue={setZip}
                                                        readOnly
                                                        className={styles.UserInput}
                                                    />
                                                    <div className={styles.UserInput}>
                                                        <CountrySelect country={country} readOnly />
                                                    </div>
                                                </div>
                                            </div>
                                            {addressSecond && (
                                                <>
                                                    <div className={styles.TitleAlone}>
                                                        <Title text={t('addressSecond')} />
                                                        {showAddressSecond ? (
                                                            <KeyboardArrowUp
                                                                onClick={() => setShowAddressSecond(false)}
                                                                className={styles.Icon}
                                                            />
                                                        ) : (
                                                            <KeyboardArrowDown
                                                                onClick={() => setShowAddressSecond(true)}
                                                                className={styles.Icon}
                                                            />
                                                        )}
                                                    </div>
                                                    {showAddressSecond && (
                                                        <div className={styles.InputContainer}>
                                                            <div className={styles.Input}>
                                                                <UserInput
                                                                    label={t('addressSecond')}
                                                                    value={addressSecond}
                                                                    setValue={setAddressSecond}
                                                                    readOnly
                                                                    className={styles.UserInput}
                                                                />
                                                                <UserInput
                                                                    label={t('citySecond')}
                                                                    value={citySecond}
                                                                    setValue={setCitySecond}
                                                                    readOnly
                                                                    className={styles.UserInput}
                                                                />
                                                            </div>
                                                            <div className={styles.Input}>
                                                                <UserInput
                                                                    label={t('zipCodeSecond')}
                                                                    value={zipSecond}
                                                                    setValue={setZipSecond}
                                                                    readOnly
                                                                    className={styles.UserInput}
                                                                />
                                                                <UserInput
                                                                    label={t('countrySecond')}
                                                                    value={countrySecond}
                                                                    setValue={setCountrySecond}
                                                                    readOnly
                                                                    className={styles.UserInput}
                                                                />
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    )}
                    {errorParent && <ErrorMessage error={t('generalError')} />}
                </div>
                <div className={styles.ChildPlacement}>
                    {isNanny && parentDaycare?.isRpeOrLaep && childs.length > 0 && (
                        <RpeComplementaryInformations
                            sections={sections}
                            groups={groups}
                            parentId={parentId}
                            daycare={parentDaycare}
                            childs={childs}
                            selectOptionsDaycare={selectOptionsDaycare}
                            handleSetSelectedDaycare={handleSetSelectedDaycare}
                            canUpdate={user?.currentDaycareRights.canWriteFamilies || false}
                        />
                    )}
                    {dataParent && (
                        <ChildTable
                            daycare={parentDaycare}
                            parentId={parentId}
                            childs={childs}
                            setChilds={setChilds}
                            isNanny={isNanny}
                            sections={sections}
                            groups={groups}
                            parentName={firstname + ' ' + lastname}
                            selectOptionsDaycare={selectOptionsDaycare}
                            handleSetSelectedDaycare={handleSetSelectedDaycare}
                            isRpeOrLaep={parentDaycare?.isRpeOrLaep ?? false}
                            handleRefetchParent={handleRefetchParent}
                            haveDaycare={parentDaycaresList.length >= 1}
                        />
                    )}
                    {parentDaycare &&
                        user?.currentDaycareRights.canReadTaskNote &&
                        user.currentDaycare?.taskNoteAccess && (
                            <TaskAndNotesParent
                                daycare={parentDaycare}
                                parentId={parentId}
                                childs={childs}
                                parentName={firstname + ' ' + lastname}
                                handleRefetchParent={handleRefetchParent}
                                canUpdate={user.currentDaycareRights.canWriteTaskNote}
                            />
                        )}
                    {errorParent && <ErrorMessage error={t('generalError')} />}
                </div>
                <div className={styles.ButtonsPlacement}>
                    <ButtonTextIcon type={'return'} onClick={handleGoBack} className={styles.Button} />
                    {user?.currentDaycareRights.canWriteFamilies && parentDaycare && parentDaycaresList.length >= 1 ? (
                        <>
                            <ButtonTextIcon
                                type={'custom'}
                                onClick={() => setDetachFamilyDialog(true)}
                                customIcon={<LinkOff />}
                                className={styles.Button}
                                customText={t('dissociateParent')}
                                disabled={!user?.currentDaycareRights.canWriteFamilies}
                            />
                            <DetachFamilyDialog
                                open={isDetachFamilyDialog}
                                handleClose={() => setDetachFamilyDialog(false)}
                                familyName={lastname}
                                familyEmail={email}
                                familyId={parentId}
                                daycareId={parentDaycare.daycareId}
                                isRpeOrLaep={parentDaycare.isRpeOrLaep}
                            />
                        </>
                    ) : user?.currentDaycareRights.canWriteSuggestions && parentDaycaresList.length === 0 ? (
                        <>
                            <ButtonTextIcon
                                type={'custom'}
                                customText={t('associate')}
                                customIcon={<LinkSharp />}
                                onClick={() => setConfirmAssociateDialogOpen(true)}
                                className={styles.Button}
                                disabled={!user?.currentDaycareRights.canWriteFamilies}
                            />
                            {isConfirmAssociateDialogOpen && email !== '' && (
                                <ConfirmAssociateParentDialog
                                    open={isConfirmAssociateDialogOpen}
                                    closeDialog={() => setConfirmAssociateDialogOpen(false)}
                                    name={lastname + ' ' + firstname}
                                    email={email}
                                    isNanny={isNanny}
                                    refetchAllSuggest={handleRefetchParent}
                                />
                            )}
                        </>
                    ) : null}
                </div>
            </div>
        </>
    )
}

export default ParentProfile
